import dayjs from 'dayjs';

// Часовые пояса РФ. value — смещение от UTC в ISO-формате (как в билете), МСК = +03:00.
export const MSK_OFFSET = '+03:00';

export const RU_TIMEZONES: { value: string; label: string }[] = [
  { value: '+02:00', label: 'Калининград (UTC+2)' },
  { value: '+03:00', label: 'Москва (UTC+3, МСК)' },
  { value: '+04:00', label: 'Самара (UTC+4)' },
  { value: '+05:00', label: 'Екатеринбург (UTC+5)' },
  { value: '+06:00', label: 'Омск (UTC+6)' },
  { value: '+07:00', label: 'Красноярск (UTC+7)' },
  { value: '+08:00', label: 'Иркутск (UTC+8)' },
  { value: '+09:00', label: 'Якутск (UTC+9)' },
  { value: '+10:00', label: 'Владивосток (UTC+10)' },
  { value: '+11:00', label: 'Магадан (UTC+11)' },
  { value: '+12:00', label: 'Камчатка (UTC+12)' },
];

export const offsetToMinutes = (offset: string): number => {
  if (!offset || offset === 'Z') return 0;
  const m = offset.match(/^([+-])(\d{2}):(\d{2})$/);
  if (!m) return 0;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3]));
};

export interface TripTimeParts {
  wall: string;   // YYYY-MM-DDTHH:mm — местное время «как в билете» (для datetime-local)
  offset: string; // +HH:MM — часовой пояс рейса
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

// Стенное время по МСК из UTC-инстанта (PB хранит date-поле в UTC и теряет пояс).
// Сдвигаем инстант на смещение МСК и читаем UTC-компоненты — получаем «часы на стене».
const utcInstantToMskWall = (date: string, time: string): string => {
  const [Y, Mo, D] = date.split('-').map(Number);
  const [H, Mi] = time.split(':').map(Number);
  const shifted = new Date(Date.UTC(Y, Mo - 1, D, H, Mi) + offsetToMinutes(MSK_OFFSET) * 60000);
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`
    + `T${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}`;
};

// Разбирает хранимое значение. Поддерживает legacy-формат YYYY-MM-DD (только дата).
export const parseTripTime = (value?: string | null): TripTimeParts | null => {
  if (!value) return null;
  const s = value.trim();

  // Полный ISO с датой и временем (+ опциональное смещение). Разделитель — T или пробел (PB отдаёт пробел).
  const full = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?/);
  if (full) {
    // PB вернул инстант в UTC (Z) — конвертируем в стенное время по МСК.
    // Значение из формы всегда с явным смещением (+HH:MM) — берём стенное время как есть.
    if (full[3] === 'Z') {
      return { wall: utcInstantToMskWall(full[1], full[2]), offset: MSK_OFFSET };
    }
    return {
      wall: `${full[1]}T${full[2]}`,
      offset: full[3] || MSK_OFFSET,
    };
  }

  // Legacy: только дата
  const dateOnly = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnly) return { wall: `${dateOnly[1]}T00:00`, offset: MSK_OFFSET };

  return null;
};

// Собирает хранимое ISO-значение из местного времени и пояса.
export const buildTripTime = (wall: string, offset: string): string => {
  if (!wall) return '';
  return `${wall}:00${offset}`;
};

// Местное время рейса «как в билете». Возвращает 'HH:mm, DD.MM' или ''.
export const toLocalDisplay = (value?: string | null): string => {
  const parts = parseTripTime(value);
  if (!parts) return '';
  return dayjs(parts.wall).format('HH:mm, DD.MM');
};

const RU_MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Компактная сводка рейса для карточки: дата «1 сен», местное и московское время.
export const formatTripLeg = (value?: string | null): { date: string; time: string; mskTime: string } | null => {
  const parts = parseTripTime(value);
  if (!parts) return null;
  const local = dayjs(parts.wall);
  const shift = offsetToMinutes(MSK_OFFSET) - offsetToMinutes(parts.offset);
  return {
    date: `${local.date()} ${RU_MONTHS_SHORT[local.month()]}`,
    time: local.format('HH:mm'),
    mskTime: local.add(shift, 'minute').format('HH:mm'),
  };
};

// Пересчёт местного времени рейса в московское. Возвращает 'HH:mm, DD.MM' или ''.
export const toMskDisplay = (value?: string | null): string => {
  const parts = parseTripTime(value);
  if (!parts) return '';
  const shift = offsetToMinutes(MSK_OFFSET) - offsetToMinutes(parts.offset);
  // wall без пояса парсится в браузерном tz, сдвиг арифметический — tz сокращается.
  return dayjs(parts.wall).add(shift, 'minute').format('HH:mm, DD.MM');
};
